/**
 * SalesNotificationsModule Component
 * Handles sales notifications display
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SalesNotificationsModule = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchNotifications = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      setError('');
      const response = await axios.get(`${API}/sales/notifications`);
      setNotifications(response.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
      if (showLoading) setError('Unable to load notifications. Please try again.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(true);
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => fetchNotifications(false), 30000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/sales/notifications/${notificationId}/read`);
      setNotifications((prev) => prev.map((item) => (
        item.id === notificationId ? { ...item, read: true } : item
      )));
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/sales/notifications/read-all`);
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800" data-testid="sales-notifications-title">Notifications</h1>
            <p className="text-sm text-gray-500">Estimate, invoice & change order activity updates</p>
          </div>
          <button
            onClick={markAllRead}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
            data-testid="sales-notifications-mark-all-read"
            type="button"
          >
            Mark all read
          </button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-6">
        {loading && (
          <div className="text-sm text-gray-500" data-testid="sales-notifications-loading">Loading notifications...</div>
        )}
        {error && (
          <div className="text-sm text-red-600" data-testid="sales-notifications-error">{error}</div>
        )}
        {!loading && !error && notifications.length === 0 && (
          <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center text-gray-500" data-testid="sales-notifications-empty">
            No notifications yet.
          </div>
        )}
        {!loading && !error && notifications.length > 0 && (
          <div className="space-y-4" data-testid="sales-notifications-list">
            {notifications.map((notification) => {
              const isChangeOrder = notification.type?.startsWith('change_order') || notification.entity_type === 'change_order';
              const isEstimate = notification.type?.startsWith('estimate') || notification.entity_type === 'estimate';
              const typeLabel = isChangeOrder ? 'Change Order' : isEstimate ? 'Estimate' : 'Invoice';
              const typeBg = isChangeOrder ? 'bg-orange-100 text-orange-700' : isEstimate ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700';
              return (
              <div
                key={notification.id}
                className={`rounded-lg border p-4 bg-white ${notification.read ? 'border-gray-200' : 'border-emerald-300 shadow-sm'}`}
                data-testid={`sales-notification-${notification.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${typeBg}`}>{typeLabel}</span>
                      <p className="text-sm font-semibold text-gray-800">{notification.title}</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {notification.created_at ? new Date(notification.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  {!notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
                      data-testid={`sales-notification-read-${notification.id}`}
                      type="button"
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesNotificationsModule;
